<template>
  <div>
    <common-data-table
      v-if="adminView || userData != null"
      :headers="computedHeaders"
      collectionName="jobs"
      :showSelect="adminView"
      :search="tableSearch"
      v-model="selectedTasks"
    >
      <template v-slot:item="{ item }">
        <tr :key="item.id" @click="rowClicked(item)">
          <td v-if="adminView">
            <v-checkbox
              :value="taskSelected(item)"
              @change="toggleSelectedTask(item)"
              @click.native.stop
            ></v-checkbox>
          </td>
          <td>{{ item.name }}</td>
          <td>{{ getLocationName(item) }}</td>
          <td>
            <user-avatar v-if="item.owner" :userInfo="item.owner" />
          </td>
          <td>{{ item.dueDate ? formattedDate(item.dueDate) : '' }}</td>
          <td>
            <status-display :status="item.status"></status-display>
          </td>
          <td v-if="adminView"></td>
          <td v-if="adminView">
            <user-avatar
              v-for="av in getVisibleAssignees(item)"
              :key="av.id"
              :userInfo="av"
            />
            <v-avatar
              v-if="getExtraAssigneesCount(item) > 0"
              class="mx-1"
              width="30"
              height="30"
              min-width="30"
              color="grey"
              >+{{ getExtraAssigneesCount(item) }}</v-avatar
            >
          </td>
        </tr>
      </template>
    </common-data-table>
    <v-dialog v-model="bulkUpdatePopupVisible" max-width="600px" persistent>
      <common-editing-popup
        title="Bulk update tasks"
        :subtitle="selectedTasks.length + ' task(s) selected'"
        saveBtnTitle="Update tasks"
        @close="bulkUpdatePopupVisible = false"
        :originalItem="{}"
        @submit="bulkUpdate"
        :loading="bulkUpdating"
      >
        <template v-slot:default="{ editedItem, formStatus }">
          <bulk-update-popup
            :dataToUpdate="editedItem"
            :formStatus="formStatus"
          ></bulk-update-popup>
        </template>
      </common-editing-popup>
    </v-dialog>
  </div>
</template>

<script>
import CommonDataTable from '@/components/CommonDataTable.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import StatusDisplay from '@/components/StatusDisplay.vue'
import { getInitials, formattedDate } from '@/common'
import { mapActions, mapGetters } from 'vuex'
import CommonEditingPopup from '@/components/CommonEditingPopup.vue'
import BulkUpdatePopup from './components/BulkUpdatePopup.vue'
import { db } from '@/firebaseConfig'
import { objects } from '@/helpers'

export default {
  name: 'TaskList',
  ref: 'test',
  props: {
    bulkEditing: {
      type: Boolean,
      default: false,
    },
    showBulkUpdate: {
      type: Boolean,
      default: false,
    },
    userData: {
      type: Object,
      default: null,
    },
    adminView: {
      type: Boolean,
      default: false,
    },
    tableSearch: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    CommonDataTable,
    UserAvatar,
    StatusDisplay,
    CommonEditingPopup,
    BulkUpdatePopup,
  },
  data() {
    return {
      headers: [
        {
          text: 'Title',
          value: 'name',
        },
        {
          text: 'Location',
          value: 'location.name',
        },
        {
          text: 'Owner',
          value: 'owner',
        },
        {
          text: 'Due',
          value: 'dueDate',
        },
        {
          text: 'Status',
          value: 'status',
        },
      ],
      getInitials,
      formattedDate,
      selectedTasks: [],
      bulkUpdating: false,
      bulkUpdatePopupVisible: this.showBulkUpdate,
    }
  },
  watch: {
    selectedTasks: {
      handler(newVal) {
        this.$emit('update:bulkEditing', newVal.length > 0)
      },
      deep: true,
    },
    bulkEditing(newVal) {
      if (newVal === false) {
        this.selectedTasks = []
      }
    },
    showBulkUpdate(newVal) {
      this.bulkUpdatePopupVisible = newVal
    },
    bulkUpdatePopupVisible(newVal) {
      this.$emit('update:showBulkUpdate', newVal)
    },
  },
  computed: {
    ...mapGetters(['companyId']),
    computedHeaders() {
      const result = this.headers
      if (this.adminView) {
        result.push(
          {
            text: 'Tags',
            value: 'tags',
            sortable: false,
          },
          {
            text: 'Assignees',
            value: 'peopleList',
            sortable: false,
          }
        )
      }

      return result
    },
  },
  methods: {
    ...mapActions(['showError', 'showSuccess']),
    async bulkDeleteAction() {
      const result = await this.$confirm(
        `Please confirm you want to delete ${this.selectedTasks.length} task(s)`,
        { title: `Delete ${this.selectedTasks.length} task(s)` }
      )

      if (result === true) {
        try {
          this.bulkUpdating = true
          const batch = db.batch()

          this.selectedTasks.forEach((task) => {
            const taskRef = db
              .collection(`companies/${this.companyId}/jobs`)
              .doc(task.id)

            batch.delete(taskRef)
          })

          await batch.commit()
          const itemsCount = this.selectedTasks.length
          this.showSuccess(`${itemsCount} task(s) successfully deleted`)
          this.selectedTasks = []
        } catch (error) {
          console.error()
          this.showError(
            '<b>Failed to delete</b>\n' + error.message ??
              'Please try again later or contact support'
          )
        } finally {
          this.bulkUpdating = false
        }
      }
    },
    toggleSelectedTask(task) {
      const taskInSelected = this.selectedTasks.find((e) => e.id === task.id)

      if (!taskInSelected) {
        this.selectedTasks.push(task)
      } else {
        const idx = this.selectedTasks.indexOf(taskInSelected)
        this.selectedTasks.splice(idx, 1)
      }
    },
    taskSelected(task) {
      const taskInSelected = this.selectedTasks.find((e) => e.id === task.id)
      return taskInSelected !== undefined
    },
    async bulkUpdate(dataToUpdate) {
      try {
        this.bulkUpdating = true
        const batch = db.batch()

        this.selectedTasks.forEach((task) => {
          const taskRef = db
            .collection(`companies/${this.companyId}/jobs`)
            .doc(task.id)
          batch.update(taskRef, objects.withUpdatedDataInfo(dataToUpdate))
        })

        await batch.commit()
        const itemsCount = this.selectedTasks.length
        this.showSuccess(`${itemsCount} task(s) successfully updated`)
        this.selectedTasks = []
        this.bulkUpdatePopupVisible = false
      } catch (error) {
        console.error(error)
        this.showError(
          '<b>Failed to update.</b>\n' + error.message ??
            'Please try again later'
        )
      } finally {
        this.bulkUpdating = false
      }
    },
    rowClicked(data) {
      this.$router.push({
        name: 'job-details',
        params: { id: data.id },
      })
    },
    getProjectName(item) {
      return item.project?.name ?? ''
    },
    getLocationName(item) {
      return item.location?.name ?? ''
    },
    getVisibleAssignees(item) {
      const result = []

      for (let i = 0; i < Math.min(item.assignees?.length ?? 0, 2); i++) {
        result.push(item.assignees[i])
      }

      return result
    },
    getExtraAssigneesCount(item) {
      return Math.max((item.assignees?.length ?? 0) - 2, 0)
    },
    getVisibleProjectOwners(item) {
      const result = []
      for (let i = 0; i < Math.min(item.project?.owners?.length ?? 0, 2); i++) {
        result.push(item.project.owners[i])
      }
      return result
    },
    getExtraProjectOnwersCount(item) {
      return Math.max((item.project?.owners?.length ?? 0) - 2, 0)
    },
  },
}
</script>